'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import StarRating from '@/components/ui/StarRating';
import { useForm } from 'react-hook-form';

const tabs = [
  { id: 'tab-reviews', label: 'Reviews', count: 22340 },
  { id: 'tab-discussions', label: 'Discussions', count: 847 },
  { id: 'tab-details', label: 'Details', count: null },
];

const reviews = [
  { id: 'rev-oph-001', user: 'Sofia Reyes', avatar: 'SR', rating: 5, title: 'A once-in-a-generation film', body: "Christopher Nolan has outdone himself with Oppenheimer. The non-linear narrative structure, which initially seems like a gimmick, reveals itself to be essential to understanding the moral complexity of Oppenheimer's story. Cillian Murphy gives a career-defining performance, conveying a man who is simultaneously brilliant, flawed, idealistic, and ultimately tragic. The IMAX sequences of the Trinity test are among the most viscerally powerful moments in cinema history.", date: 'Jul 22, 2023', helpful: 2847, spoiler: false, verified: true },
  { id: 'rev-oph-002', user: 'James Okafor', avatar: 'JO', rating: 5, title: 'Technically and emotionally staggering', body: "The sound design alone deserves every award it receives. Nolan makes the bold choice to show the Trinity test without sound first, and then with — the contrast is devastating. Robert Downey Jr. is also extraordinary in a role I never expected to see him in. The courtroom sequences in the second half are as tense as any action film.", date: 'Aug 5, 2023', helpful: 1923, spoiler: false, verified: true },
  { id: 'rev-oph-003', user: 'Priya Nair', avatar: 'PN', rating: 4, title: 'Brilliant but demanding', body: "Three hours is a commitment, and Nolan asks a lot of his audience. The middle section drags slightly as we wade through the security hearing. But when the film is firing on all cylinders — particularly the first hour of scientific discovery and the Trinity test — it is absolutely breathtaking. Emma Thomas's production design is impeccable.", date: 'Aug 12, 2023', helpful: 1456, spoiler: false, verified: false },
  { id: 'rev-oph-004', user: 'Tomas Novak', avatar: 'TN', rating: 5, title: 'The moral weight of discovery', body: "What makes Oppenheimer exceptional is that it refuses easy answers. This is not a film that condemns Oppenheimer or celebrates him — it presents the full complexity of a man who helped end one war and potentially enabled the destruction of civilization. The epilogue hits like a gut punch.", date: 'Sep 3, 2023', helpful: 987, spoiler: true, verified: true },
  { id: 'rev-oph-005', user: 'Amara Diallo', avatar: 'AD', rating: 4, title: 'Nolan at his most ambitious', body: "The ensemble cast is extraordinary — nearly every scene features a Hollywood legend, yet it never feels crowded. Each performance is precisely calibrated. My only complaint is that the female characters are underdeveloped relative to the men. Emily Blunt and Florence Pugh both do exceptional work with what they are given, but they deserve more.", date: 'Oct 1, 2023', helpful: 743, spoiler: false, verified: false },
  { id: 'rev-oph-006', user: 'Kenji Watanabe', avatar: 'KW', rating: 3, title: 'Technically masterful, emotionally distant', body: "I respect this film enormously but I found it difficult to emotionally connect with. Nolan's clinical precision, which serves him so well in films like Inception and Dunkirk, creates a certain coldness here that prevented me from fully investing in Oppenheimer as a human being rather than a historical figure.", date: 'Nov 15, 2023', helpful: 612, spoiler: false, verified: true },
];

interface ReviewFormData {
  title: string;
  body: string;
  rating: number;
  hasSpoilers: boolean;
}

export default function MovieTabsSection() {
  const [activeTab, setActiveTab] = useState('tab-reviews');
  const [spoilerRevealed, setSpoilerRevealed] = useState<Record<string, boolean>>({});
  const [sortReviews, setSortReviews] = useState<'helpful' | 'recent' | 'rating'>('helpful');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [userRating, setUserRating] = useState(0);

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<ReviewFormData>();

  const sortedReviews = [...reviews].sort((a, b) => {
    if (sortReviews === 'helpful') return b.helpful - a.helpful;
    if (sortReviews === 'rating') return b.rating - a.rating;
    return 0;
  });

  const onSubmit = async (data: ReviewFormData) => {
    // Backend integration: POST /api/reviews with { movieId, ...data, rating: userRating }
    await new Promise((r) => setTimeout(r, 1200));
    reset();
    setUserRating(0);
    setShowReviewForm(false);
  };

  return (
    <div className="mt-4">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-600 border-b-2 transition-colors duration-150 -mb-px ${
              activeTab === tab.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            {tab.count !== null && (
              <span className="text-xs font-500 text-muted-foreground">({tab.count.toLocaleString()})</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'tab-reviews' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Reviews list */}
          <div className="xl:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-700 text-foreground">Community Reviews</h3>
              <div className="flex items-center gap-2">
                <select
                  value={sortReviews}
                  onChange={(e) => setSortReviews(e.target.value as typeof sortReviews)}
                  className="input-dark text-xs px-2.5 py-1.5"
                >
                  <option value="helpful">Most Helpful</option>
                  <option value="recent">Most Recent</option>
                  <option value="rating">Highest Rated</option>
                </select>
                <button
                  onClick={() => setShowReviewForm(!showReviewForm)}
                  className="btn-primary flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-600"
                >
                  <Icon name="PencilIcon" size={12} />
                  Write Review
                </button>
              </div>
            </div>

            {/* Review form */}
            {showReviewForm && (
              <div className="mb-6 p-5 rounded-2xl bg-card border border-border scale-in">
                <h4 className="text-sm font-700 text-foreground mb-4">Write Your Review</h4>
                <div className="mb-4">
                  <label className="block text-xs font-500 text-muted-foreground mb-2">Your Rating</label>
                  <StarRating value={userRating} interactive onChange={setUserRating} size={24} showValue={userRating > 0} />
                  {userRating === 0 && <p className="text-xs text-muted-foreground/60 mt-1">Select a rating to continue</p>}
                </div>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label className="block text-xs font-500 text-muted-foreground mb-1.5">Review Title</label>
                    <input
                      {...register('title', { required: 'Title is required', minLength: { value: 5, message: 'Minimum 5 characters' } })}
                      type="text"
                      placeholder="Summarize your thoughts..."
                      className="input-dark w-full px-3 py-2.5 text-sm"
                    />
                    {errors.title && <p className="text-xs text-primary mt-1">{errors.title.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-muted-foreground mb-1.5">Your Review</label>
                    <textarea
                      {...register('body', { required: 'Review text is required', minLength: { value: 50, message: 'Minimum 50 characters' } })}
                      rows={5}
                      placeholder="Share your detailed thoughts on the film..."
                      className="input-dark w-full px-3 py-2.5 text-sm resize-none"
                    />
                    {errors.body && <p className="text-xs text-primary mt-1">{errors.body.message}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <input {...register('hasSpoilers')} type="checkbox" id="spoiler-flag" className="rounded" />
                    <label htmlFor="spoiler-flag" className="text-xs text-muted-foreground">Contains spoilers</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={isSubmitting || userRating === 0}
                      className="btn-primary px-6 py-2.5 rounded-lg text-sm font-600 flex items-center gap-2 min-w-[120px] justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                          Submitting...
                        </>
                      ) : 'Submit Review'}
                    </button>
                    <button type="button" onClick={() => setShowReviewForm(false)} className="btn-ghost px-4 py-2.5 rounded-lg text-sm">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {/* Reviews */}
            <div className="space-y-4">
              {sortedReviews.map((review) => (
                <div key={review.id} className="p-5 rounded-2xl bg-card border border-border card-hover">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/60 to-purple-800/60 flex items-center justify-center text-sm font-700 text-white flex-shrink-0">
                        {review.avatar}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-600 text-foreground">{review.user}</span>
                          {review.verified && (
                            <span className="flex items-center gap-0.5 text-xs text-green-400">
                              <Icon name="CheckBadgeIcon" variant="solid" size={12} />
                              Verified
                            </span>
                          )}
                          {review.spoiler && (
                            <span className="px-1.5 py-0.5 rounded text-xs font-600 status-flagged">Spoilers</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <StarRating value={review.rating} size={11} />
                          <span className="text-xs text-muted-foreground">{review.date}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <h4 className="text-sm font-700 text-foreground mb-2">{review.title}</h4>
                  <div
                    className={review.spoiler && !spoilerRevealed[review.id] ? 'spoiler-blur' : ''}
                    onClick={() => review.spoiler && setSpoilerRevealed(prev => ({ ...prev, [review.id]: true }))}
                  >
                    <p className="text-sm text-muted-foreground leading-relaxed">{review.body}</p>
                  </div>
                  {review.spoiler && !spoilerRevealed[review.id] && (
                    <p className="text-xs text-muted-foreground/60 mt-1 cursor-pointer" onClick={() => setSpoilerRevealed(prev => ({ ...prev, [review.id]: true }))}>
                      Click to reveal spoiler content
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border">
                    <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                      <Icon name="HandThumbUpIcon" size={13} />
                      Helpful ({review.helpful.toLocaleString()})
                    </button>
                    <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                      <Icon name="FlagIcon" size={13} />
                      Report
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar stats */}
          <div className="xl:col-span-1 space-y-4">
            <div className="p-4 rounded-2xl bg-card border border-border">
              <h4 className="text-sm font-700 text-foreground mb-3">Review Stats</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Total Reviews', value: '22.3K' },
                  { label: 'Avg Rating', value: '4.8' },
                  { label: 'Recommend', value: '96%' },
                  { label: 'This Month', value: '+340' },
                ].map((stat) => (
                  <div key={`stat-${stat.label}`} className="text-center p-2 rounded-lg bg-muted/50">
                    <div className="text-lg font-800 text-accent font-mono-nums">{stat.value}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-card border border-border">
              <h4 className="text-sm font-700 text-foreground mb-3">Audience Verdict</h4>
              <div className="space-y-2">
                {[
                  { label: 'Cinematography', score: 98 },
                  { label: 'Acting', score: 97 },
                  { label: 'Story', score: 91 },
                  { label: 'Pacing', score: 82 },
                  { label: 'Score/Music', score: 96 },
                ].map((item) => (
                  <div key={`verdict-${item.label}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">{item.label}</span>
                      <span className="text-xs font-700 text-accent font-mono-nums">{item.score}%</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-primary to-accent rounded-full progress-bar-fill" style={{ width: `${item.score}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'tab-details' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          {[
            { label: 'Original Title', value: 'Oppenheimer' },
            { label: 'Director', value: 'Christopher Nolan' },
            { label: 'Writers', value: 'Christopher Nolan' },
            { label: 'Release Date', value: 'July 21, 2023' },
            { label: 'Runtime', value: '3h 0m' },
            { label: 'Country', value: 'United States' },
            { label: 'Language', value: 'English, German, French' },
            { label: 'Budget', value: '$100,000,000' },
            { label: 'Box Office', value: '$952,000,000' },
            { label: 'Production', value: 'Universal Pictures, Syncopy' },
          ].map((item) => (
            <div key={`detail-${item.label}`} className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground/60 uppercase tracking-wider">{item.label}</span>
              <span className="text-sm font-500 text-foreground">{item.value}</span>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'tab-discussions' && (
        <div className="text-center py-12">
          <Icon name="ChatBubbleLeftRightIcon" size={40} className="text-muted mx-auto mb-3" />
          <p className="text-base font-600 text-foreground mb-1">847 active discussions</p>
          <p className="text-sm text-muted-foreground mb-4">Join the conversation about this film</p>
          <a href="/community-discussions" className="btn-primary px-6 py-2.5 rounded-xl text-sm font-600 inline-block">
            View Discussions
          </a>
        </div>
      )}
    </div>
  );
}